import { keepPreviousData } from "@tanstack/react-query";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportsClient } from "../reports/ReportsClientContext";
import { useReport } from "../reports/useReport";
import { DraftsToApproveCard } from "../ai/drafts/DraftsToApproveCard"; // GAVION ai-email
import { CallbacksToday, type Callback } from "./CallbacksToday";
import {
  endOfTodayIso,
  fillDays,
  last14Range,
  startOfTodayIso,
  todayRange,
  weekRange,
} from "./dashboardDates";
import { KpiTile } from "./KpiTile";
import { MyDayGoals } from "./MyDayGoals";
import { RankingCard } from "./RankingCard";
import { Sparkline } from "./Sparkline";
import { StartCallingCta } from "./StartCallingCta";
import { UpcomingDemos } from "./UpcomingDemos";
import { useNow } from "./useNow";
import { TrainingNudge } from "../training/TrainingNudge"; // GAVION training

/**
 * Rep view (spec 09 §4): today's counters, "Ma journée" goals (X1), callbacks, demos, the "Classement" (X1) and a
 * 14-day trend. `now` ticks every
 * minute; report and list keys use Toronto dates, so they only change when the day does.
 */
export const RepDashboard = () => {
  const translate = useTranslate();
  const client = useReportsClient();
  const { identity } = useGetIdentity();
  const ready = identity != null;
  const salesId = Number(identity?.id ?? 0);
  const now = useNow(60_000);
  const today = todayRange(now);
  const week = weekRange(now);
  const days14 = last14Range(now);
  const me = { scope: "me" as const, sales_id: salesId };
  const todayQ = useReport(
    ["activity", "me", salesId, today.from, today.to],
    () => client.activity(today.from, today.to, me),
    { enabled: ready },
  );
  const weekQ = useReport(
    ["activity", "me", salesId, week.from, week.to],
    () => client.activity(week.from, week.to, me),
    { enabled: ready },
  );
  const trendQ = useReport(
    ["activity", "me", salesId, days14.from, days14.to],
    () => client.activity(days14.from, days14.to, me),
    { enabled: ready },
  );
  const { data: callbacks = [], total: callbackTotal } = useGetList<Callback>(
    "companies",
    {
      filter: {
        assigned_sales_id: salesId,
        "next_callback_at@lt": endOfTodayIso(now),
      },
      sort: { field: "next_callback_at", order: "ASC" },
      pagination: { page: 1, perPage: 20 },
    },
    { enabled: ready, retry: false },
  );
  // Dedicated count (perPage 1 + total): the list above is capped at 20 rows.
  const { total: overdueCallbacks } = useGetList(
    "companies",
    {
      filter: {
        assigned_sales_id: salesId,
        "next_callback_at@lt": now.toISOString(),
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: ready, retry: false, placeholderData: keepPreviousData },
  );
  const { total: overdueTasks } = useGetList(
    "tasks",
    {
      filter: {
        sales_id: salesId,
        "done_date@is": null,
        "due_date@lt": startOfTodayIso(now),
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: ready, retry: false },
  );
  if (!ready || todayQ.isPending)
    return <Skeleton className="h-40 w-full rounded-xl" />;
  const dials = todayQ.data?.totals.dials;
  const trend = fillDays(trendQ.data?.by_day ?? [], days14.from, days14.to);
  return (
    <div className="space-y-6">
      {todayQ.isError || !todayQ.data ? (
        <p role="alert" className="text-sm text-destructive">
          {translate("gavion.reports.error")}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <KpiTile
            label={translate("gavion.dashboard.kpi.dials_today")}
            value={todayQ.data.totals.dials}
          />
          <KpiTile
            label={translate("gavion.dashboard.kpi.contacts_today")}
            value={todayQ.data.totals.contacts}
          />
          <KpiTile
            label={translate("gavion.dashboard.kpi.demos_week")}
            value={weekQ.data ? weekQ.data.totals.demos : "—"}
          />
          <KpiTile
            label={translate("gavion.dashboard.kpi.callbacks_due")}
            value={callbackTotal ?? 0}
            tone={overdueCallbacks ? "danger" : "default"}
            hint={
              overdueCallbacks
                ? translate("gavion.dashboard.kpi.overdue_count", {
                    smart_count: overdueCallbacks,
                  })
                : undefined
            }
          />
          <KpiTile
            label={translate("gavion.dashboard.kpi.tasks_overdue")}
            value={overdueTasks ?? 0}
            tone={overdueTasks ? "danger" : "default"}
          />
        </div>
      )}
      <TrainingNudge />
      <MyDayGoals salesId={salesId} now={now} />
      <div className="space-y-2">
        {dials === 0 && (
          <p className="text-sm font-medium">
            {translate("gavion.dashboard.ready_to_call")}
          </p>
        )}
        <StartCallingCta />
      </div>
      <DraftsToApproveCard />
      {/* GAVION wide screens: on ≥ 2560 px the ranking joins callbacks and demos on one row */}
      <div className="grid gap-4 lg:grid-cols-2 min-[160rem]:grid-cols-3">
        <CallbacksToday callbacks={callbacks} now={now} />
        <UpcomingDemos salesId={salesId} now={now} />
        {/* mt-2: the 24 px gap (space-y-6) it always had below the 16 px grid gap */}
        <div className="mt-2 min-w-0 lg:col-span-2 min-[160rem]:col-span-1 min-[160rem]:mt-0 min-[160rem]:[&>*]:h-full">
          <RankingCard salesId={salesId} now={now} showLink />
        </div>
      </div>
      <section
        aria-labelledby="gavion-my-activity"
        className="rounded-xl border bg-card p-4"
      >
        <h2 id="gavion-my-activity" className="mb-3 text-lg font-bold">
          {translate("gavion.dashboard.my_activity")}
        </h2>
        <div className="flex flex-wrap gap-8">
          <Sparkline
            label={translate("gavion.dashboard.dials")}
            points={trend.map((d) => ({ day: d.day, value: d.dials }))}
          />
          <Sparkline
            label={translate("gavion.dashboard.contacts")}
            points={trend.map((d) => ({ day: d.day, value: d.contacts }))}
          />
        </div>
      </section>
    </div>
  );
};
